import React, { useRef, useCallback, useState, useEffect, } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Api from '../../libs/Api';
import { densityOptions, groupedColors, windowLevelWidthOptions } from '../../ui/variables';
import { Row, Col } from 'reactstrap';

import { NiftiEditorToolStyle, EditorPreviewWrapper, BodyWrapper, CanvasWrapper, InputsWrapper, OriginalPreview, MaskiaCanvas, SegCanvas, EditorPreview, Range, DensityRange, Label, Span } from './NiftiEditorToolStyle';

import PropTypes from 'prop-types';

import Header from '../../components/Header/Header';
import ActionButton from '../../components/ActionButton/ActionButton';
import Loading from '../../components/Loading/Loading';
import ColorSelector from '../../components/ColorSelector/ColorSelector';
import DensityTypesSelect from '../../components/DensityTypeSelect/DensityTypeSelect.js';
import WindowWidthLevelSelect from '../../components/WindowWidthLevelSelect/WindowWidthLevelSelect.js';
import InputNumberField from '../../components/InputNumberField/InputNumberField';
import OptionList from '../../components/OptionList/OptionList';
import Checkbox from '../../components/Checkbox/Checkbox';
import translate from '../../libs/i18n';

import { useIsMounted } from '../../utils/useIsMounted';
import roundToPrecision from '../../utils/roundToPrecision';

import BrushIcon from '../../assets/svgs/BrushIcon';
import RefreshIcon from '../../assets/svgs/RefreshIcon';
import ZoomInIcon from '../../assets/svgs/ZoomInIcon';
import ZoomOutIcon from '../../assets/svgs/ZoomOutIcon';
import EraserIcon from '../../assets/svgs/EraserIcon';
import DownloadIcon from '../../assets/svgs/DownloadIcon';
import SendIcon from '../../assets/svgs/SendIcon';
import UndoIcon from '../../assets/svgs/UndoIcon';

const MIN_SPHERE_DIAMETER = 1;
const MAX_SPHERE_DIAMETER = 10;

const NiftiEditorTool = ({ modal }) => {
  const language = useSelector((state) => state.settings.language);

  const [eraser, setEraser] = useState(false);
  const [errorExamModal, setErrorExamModal] = useState(false);
  const [uploadSuccessModal, setUploadSuccessModal] = useState(false);
  const [errorUploadModal, setErrorUploadModal] = useState(false);
  const [brush, setBrush] = useState(false);
  const [loading, setLoading] = useState(true);
  const [is3D, setIs3D] = useState(false);
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [actualSlice, setActualSlice] = useState();
  const [maxSlice, setMaxSlice] = useState(1);
  const [opacityValue, setOpacityValue] = useState(80);
  const [scale, setScale] = useState(1.5);
  const [allowOverwrite, setAllowOverwrite] = useState(false);
  const [invertImage, setInvertImage] = useState(false);

  const [isSavingFile, setIsSavingFile] = useState(false);
  const [hasFile, setHasFile] = useState(false);
  const { id } = useParams();
  const isMounted = useIsMounted();
  const [report, setReport] = useState();

  const [selectedType, setSelectedType] = useState(densityOptions[4]);
  const [selectedWidthLevelOriginal, setSelectedWidthLevelOriginal] = useState(windowLevelWidthOptions[3]);
  const [selectedWidthLevelAnnotation, setSelectedWidthLevelAnnotation] = useState(windowLevelWidthOptions[3]);
  const [selectedColor, setSelectedColor] = useState();

  const [nifti, setNifti] = useState();
  const [mask, setMask] = useState();
  const [segEnsemble, setSegEnsemble] = useState();

  const [segmentationDiameter, setSegmentationDiameter] = useState(5);
  const [maxDensityLimits, setMaxDensityLimits] = useState(
    2000
  );
  const [minDensityLimits, setMinDensityLimits] = useState(
    -2000
  );
  const history = useHistory();
  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  // Pega da API as informações do paciente de acordo com o ID na URL do site
  useEffect(() => {
    (async () => {
      if (id?.length) {
        if (!isMounted()) {
          return;
        }

        const reportResponse = await Api.getReport(id);
        let currentReport = null;

        if (reportResponse?.status === 200 && reportResponse?.data) {
          currentReport = reportResponse?.data;
        }

        if (reportResponse?.status === 404 || !currentReport) {
          history.push('/404-exame');
        } else if (reportResponse?.status === 200 && currentReport) {
          setReport(currentReport);
          window.AppState.resetGlobals();
        }
      }
    })();
  }, [id, isMounted, history]);

  useEffect(() => {
    const fetchFile = async (type, apiCall, fileSetter, readFileIndex) => {
      try {
        if (report?.id && !report[type]) {
          console.log(`Requisitando ${type} do Orthanc`);
          const response = await apiCall(report?.id);
  
          if (isMounted()){
            if (response?.status === 404 || response?.status === 400 || !response?.data) {
              setErrorExamModal(true);
              setLoading(false);
              return false;
            } else if (response?.status === 200 && response?.data) {
              setLoading(true);
              setHasFile(true);
    
              const blob = new Blob([response.data], { type: 'application/octet-stream' });
              const file = new File([blob], `${type}`, { type: blob.type });
    
              window.readFile(file, readFileIndex);
              console.log(file);
              fileSetter(file);
              await delay(500);
              setLoading(false);
              setHasFile(false);
              return true;
            }
          }
        } else if (report?.[type]) {
          console.log(`Pegando ${type} diretamente do backend`);
          const response = await fetch(report[type]);
          if (isMounted()) {
            const blob = await response.blob();
            const file = new File([blob], `${type}`, { type: blob.type });  
            
            window.readFile(file, readFileIndex);
            fileSetter(file);
            setLoading(false);
            setHasFile(false);
            return true;
          }
        }
      } catch (error) {
        if (isMounted()) {
          console.error(`Error fetching ${type}:`, error);
          setErrorExamModal(true);
          setLoading(false);
          return false;
        }
      }
    };
  
    (async () => {
      if (report?.id && isMounted()) {
        window.AppState.hasNoSegEnsemble = false;
        window.AppState.isRadiography = false
  
        const niftiSuccess = await fetchFile('nifti', Api.getCompressNiftiById, setNifti, 0);
        if (!niftiSuccess) return;
  
        await delay(500);
        if (isMounted()) {
          const maskSuccess = await fetchFile('mask', Api.getMaskIaById, setMask, 1);
          if (!maskSuccess) return;
        }
  
        await delay(500);
        if (isMounted()) {
          await fetchFile('seg_ensemble', Api.getSegEnsembleById, setSegEnsemble, 2);
        }     
      }

      //Iniciar já nas densidades de pulmão
    if(isMounted()){
      setMaxDensityLimits(densityOptions[4].maxDensity)
      window.AppState.filtroDensidadeMax = densityOptions[4].maxDensity;
      setMinDensityLimits(densityOptions[4].minDensity)
      window.AppState.filtroDensidadeMin = densityOptions[4].minDensity;
          }
    })();
  }, [report, isMounted]);
  

  const dataFileRef = useRef(null);
  const maskFileRef = useRef(null);
  const segFileRef = useRef(null);

  const saveHandler = useCallback((event) => {
    event.preventDefault();
    setIsSavingFile(true);
    exportMask();
    save_nifti('edicao_' + report?.exam?.patient?.name + '_' + report?.series_id);
    setIsSavingFile(false);
  }, [report]);

const uploadHandler = useCallback(
  async (event) => {
    setLoading(true);
    let blob = new Blob();
    event.preventDefault();

    setIsSavingFile(true);
    exportMask();
    blob = upload_nifti();
    console.log(blob);

    try {
      const segEditedResponse = await Api.getSegEnsembleEditedById(report?.id, 'edited', blob);
      await Api.markMaskSentToOrthanc(report?.id, 'edition');

      if (!isMounted()) return;
      
      console.log(segEditedResponse);
      if (segEditedResponse?.status === 200 && segEditedResponse?.data) {
        setUploadSuccessModal(true);
      } else {
        setErrorUploadModal(true);
      }
      setLoading(false);

      const timeoutId = setTimeout(() => {
        if (isMounted()) {
          setUploadSuccessModal(false);
          setErrorUploadModal(false);
        }
      }, 5000);

      setIsSavingFile(false);
      
      return () => clearTimeout(timeoutId);
    
    } catch (error) {
      if (isMounted()) {
        setErrorUploadModal(true);
        setIsSavingFile(false);
        setLoading(false);

      }
    }
  },
  [report, isMounted]
);

  const mouseDownFunction = (evt) => {
    var canvas = document.getElementById('maskCanvas');
    evt.preventDefault();

    if (
      (evt.button == 0 && evt.ctrlKey) ||
      (evt.touches && window.AppState.touchSetting == 'move')
    ) {
      let mousePos = window.getMousePos(canvas, evt);

      window.AppState.historyPosx = mousePos.x;
      window.AppState.historyPosy = mousePos.y;
    } else if (evt.button == 1) {
      window.AppState.brilhoAtivado = true;
      let mousePos = window.getMousePos(canvas, evt);
      window.AppState.historyPosx = mousePos.rX;
      window.AppState.historyPosy = mousePos.rY;
    } else if (brush && evt.button == 0) {
      window.AppState.editorAtivado = true;
    } else if (eraser && evt.button == 0) {
      window.AppState.apagadorAtivado = true;
    }
  };

  const mouseOnClickFunction = (evt) =>{
    evt.preventDefault()
    var canvas = document.getElementById(evt.target.id);
    var slider = document.getElementById('myRange');
    
    const rotationDeg = (!window.AppState.isRadiography) ? 180 : 0;
    const mousePos = window.getMousePos(canvas, evt, rotationDeg);

    window.AppState.posx = mousePos.x;
    window.AppState.posy = mousePos.y;
  
    if (brush && selectedColor && evt.button == 0 && canvas.id == 'maskCanvas') {
      window.editaMascara(mousePos, window.AppState.valorRoi); 				
      requestAnimationFrame(window.desenhaSlice);
      
     } else if (eraser && evt.button == 0 && canvas.id == 'maskCanvas') {
      window.editaMascara(mousePos, 0);
      requestAnimationFrame(window.desenhaSlice);
     }
  }

  const mouseWheel = (evt) => {
    evt.preventDefault();
    var canvas = document.getElementById('maskCanvas');
    var slider = document.getElementById('myRange');
    if (slider && canvas) {
      var x = parseInt(evt.deltaY);
      var slice = parseInt(slider.value);
      if (x < 0) {
        slice += 1;
      } else if (x > 0) {
        slice -= 1;
      }
      if (slice > 0 && slice <= slider.max) {
        slider.value = slice;
        setActualSlice(slice)
        window.voxelOnMousePosition(canvas, slider, evt)
        requestAnimationFrame(window.desenhaSlice);
      }
    }
  }

  const onClickBrush = () => {
    if (!selectedColor) {
      const foundColor = findItemByValorRoi(groupedColors, window.AppState.valorRoi);
      if (foundColor) {
        setSelectedColor(foundColor);
      } 
    }

    setBrush(!brush);
    setEraser(false);
  };
  
  const findItemByValorRoi = (groupedColors, valorRoi) => {
    for (let group of groupedColors) {
      const foundItem = group.items.find(item => item.valorRoi === valorRoi);
      if (foundItem) {
        return foundItem;
      }
    }
    return null; // Retorna null se o item não for encontrado
  };
  
  const onClickAllowOverwrite = () => {
    setAllowOverwrite(!allowOverwrite);
    window.AppState.allowOverwrite = !allowOverwrite;
  };

  const onClickEraser = () => {
    setEraser(!eraser);
    setBrush(false);
  };

  const onClickRefresh = () => {
    setShowUndoModal(true);
  };

  useEffect(() => {
    window.AppState.raio = segmentationDiameter;
  }, [segmentationDiameter]);

  const onChangeInputRangeDensityMin = (event) => {
    if (event?.target?.value?.length) {
      let currentStep = event?.target?.value;
      let minimum = event?.target?.min
      let maximum = document.getElementById("sliderDensityMax").value;;
      

      if (currentStep?.length) {
        currentStep = parseInt(currentStep, 10);

        if (currentStep <= minimum) {
          currentStep = minimum;
        }

        if (currentStep >= maximum) {
          currentStep = maximum;
        }

        setSelectedType(densityOptions.find(type => type.name == 'Any'));
        setMinDensityLimits(currentStep)
        window.AppState.filtroDensidadeMin = currentStep;
        window.desenhaSlice();
      }
    }
  };

  const onChangeInputRangeDensityMax = (event) => {
    if (event?.target?.value?.length) {
      let currentStep = event?.target?.value;
      let minimum = document.getElementById("sliderDensityMin").value;
      let maximum = event?.target?.max

      if (currentStep?.length) {
        currentStep = parseInt(currentStep, 10);

        if (currentStep <= minimum) {
          currentStep = minimum;
        }

        if (currentStep >= maximum) {
          currentStep = maximum;
        }

        setSelectedType(densityOptions.find(type => type.name == 'Any'));
        setMaxDensityLimits(currentStep)
        window.AppState.filtroDensidadeMax = currentStep;
        window.desenhaSlice();
      }
    }
  };

  const handleResetZoom = () => {
    setScale(1.5);
  };
  const onClickZoomIn = () => {
    const zoomIntensity = 0.1;
    const newScale = Math.min(scale + zoomIntensity, 2);
    setScale(newScale);
  };

  const onClickZoomOut = () => {
    const zoomIntensity = 0.1;
    const newScale = Math.max(scale - zoomIntensity, 1);
    setScale(newScale);
  };

  const onClick3D = () => {
        setIs3D(!is3D)
        window.AppState.is2D = is3D
  }
  
  const onClickInvert = () => {
    setInvertImage(prev => {
      const next = !prev;
      window.AppState.invertImage = next;
      requestAnimationFrame(window.desenhaSlice);
      return next;
    });
  }

  const handleOpacityChange = (e) => {
    const value = e.target.value;
    window.AppState.opacidadeRoi = value / 100;
    setOpacityValue(value);
    requestAnimationFrame(window.redrawMask);
  };

  const ref = useRef(null);
  const handleSliceChange = (newValue) => {
    const element = ref.current;
    const value = Math.max(1, Math.min(newValue, maxSlice));
    setActualSlice(value);
    if (element) {
      element.value = value;
      const event = new Event("input", { bubbles: true });
      element.dispatchEvent(event);
    }
  };

  useEffect(() => {
    const element = ref.current;
    if (element) {
      setMaxSlice(Number(element.max));
      setActualSlice(Math.round(element.max / 2));
    }
  }, [mask]);

  const handleSelectType = (type) => {
    if(type){
      setSelectedType(type);
      setMaxDensityLimits(type.maxDensity)
      window.AppState.filtroDensidadeMax = type.maxDensity;
      setMinDensityLimits(type.minDensity)
      window.AppState.filtroDensidadeMin = type.minDensity;
    }
  };

  const handleWidthLevelOptionsOriginal = (options, canvas) => {

    if(options){
      setSelectedWidthLevelOriginal(options);
      window.setaWindowLevel(options.windowWidth, options.windowLevel, canvas);
    }

  };

  const handleWidthLevelOptionsAnnotation = (options, canvas) => {

    if(options){
      setSelectedWidthLevelAnnotation(options);
      window.setaWindowLevel(options.windowWidth, options.windowLevel, canvas);
    }

  };


  const handleSelectColor = (color) => {
    setSelectedColor(color);
    if(color){
      window.AppState.valorRoi = color.valorRoi
      setBrush(true);
    } else {
      setBrush(false);
    }

    setEraser(false);
  };

    useEffect(() => {
      const handleKeyDown = (event) => {
        if (event.ctrlKey && event.key === 'z') {
          event.preventDefault();
          window.desfazerAlteracao();
        }
      };
  
      window.addEventListener('keydown', handleKeyDown);
  
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, []);


  useEffect(() => {
    window.AppState.allowOverwrite = allowOverwrite;
  }, [allowOverwrite]);

  return (
    <NiftiEditorToolStyle>
      {!modal ? <Header /> : null}
      <div className={modal ? 'web-visualizer-modal-container' : 'web-visualizer-container'}>

      {showUndoModal && (
        <>
          <div className="undo-modal-backdrop" onClick={() => setShowUndoModal(false)}></div>
          <div className="confirm-undo-modal-container">
            <h3 className="undo-modal-title">
              {translate('undoMaskChanges', language)}
            </h3>
            <p className="undo-modal-question">
              {translate('areYouSureUndoMaskChanges', language)}
            </p>
            <p className="undo-modal-description">
              {translate('afterConfirmationUndoMaskChanges', language)}
            </p>
            <div className="undo-modal-actions">
              <div className="modal-action-wrapper">
                <ActionButton
                  theme="light-disabled"
                  text={translate('cancel', language).toUpperCase()}
                  onClick={() => setShowUndoModal(false)}
                />
              </div>
              <div className="modal-action-wrapper">
                <ActionButton
                  theme="default"
                  text={translate('yesIDo', language).toUpperCase()}
                  onClick={() => {
                    setShowUndoModal(false);
                    window.resetarAlteracoes();
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {errorExamModal ?
        <div className="alert alert-danger" role="alert">
            {translate('niftiError', language)}
        </div> : null}

      {uploadSuccessModal ?
        <div className="alert alert-success" role="alert">
            {translate('uploadSuccess', language)}
        </div> : null}

      {errorUploadModal ?
        <div className="alert alert-danger" role="alert">
            {translate('uploadError', language)}
        </div> : null}

        <CanvasWrapper>
          <InputsWrapper>
            <div className="inputs-row inputs-row--top">
              <div className="tool tool--draw">
                <OptionList
                  theme="dark"
                  rounded
                  options={[
                    {
                      key: 1,
                      content: <BrushIcon />,
                      active: brush,
                      onClick: onClickBrush,
                    },
                    {
                      key: 2,
                      content: <EraserIcon />,
                      active: eraser,
                      onClick: onClickEraser,
                    },
                  ]}
                />
              </div>

              <div className="tool tool--class">
                <ColorSelector 
                  groupedColors={groupedColors}
                  selected={selectedColor}
                  placeholder={translate('selectAClass', language)}
                  handleSelect={handleSelectColor}>
                </ColorSelector>
              </div>

              <div className="tool tool--buttons-group">
                <div className="tool tool--overwrite">
                    <ActionButton
                      theme={allowOverwrite ? "default" : "very-dark"}
                      onClick={onClickAllowOverwrite} 
                      text={translate('overwriteExistingAnnotations', language)}
                    >
                    </ActionButton>
                </div>

                <div className="tool tool--invert">
                    <ActionButton
                      theme={invertImage ? "default" : "very-dark"}
                      onClick={onClickInvert}
                      text={translate('invertContrast', language)}
                    />
                </div>

                <div className="tool tool--window">
                    <WindowWidthLevelSelect
                              types={windowLevelWidthOptions}
                              selected={selectedWidthLevelOriginal}
                              handleSelect={(options) => handleWidthLevelOptionsOriginal(options, 'originalPreview')}
                      />
                      <WindowWidthLevelSelect
                              className="ml-2"
                              types={windowLevelWidthOptions}
                              selected={selectedWidthLevelAnnotation}
                              handleSelect={(options) => handleWidthLevelOptionsAnnotation(options, 'myCanvas')}
                      />
                </div>
              </div>

              <div className="tool tool--opacity">
                <Label htmlFor="myRangeOp" className="pb-3" style={{ width: '100%' }}>
                  <Span style={{ fontSize: '12px', display: 'flex', justifyContent: 'center' }}>{translate('opacity', language)}</Span>
                  <div className="tools-list-row segmentation-tools-rows d-flex align-items-center justify-content-between">
                    <Span className="me-2">0%</Span>
                    <div className="slider-wrapper position-relative w-100">
                      <Range 
                        type="range" 
                        min="1" 
                        max="100" 
                        defaultValue="80" 
                        className="slider" 
                        id="myRangeOp"
                        value={opacityValue}
                        onChange={(e) => handleOpacityChange(e)}
                        label="Opacidade da Máscara" />

                      <div
                        className="slider-tooltip"
                        style={{
                          left: `calc(${(opacityValue)}%)`,
                        }}
                      >
                        {opacityValue}%
                      </div>
                    </div>

                    <Span className="ms-2">100%</Span>
                  </div>
                </Label>
              </div>

              <div className="tool tool--zoom">
                    <OptionList
                      theme="dark"
                      rounded
                      options={[
                        {
                          key: 1,
                          content: <RefreshIcon />,
                          onClick: handleResetZoom,
                        },
                        {
                          key: 2,
                          content: <ZoomInIcon />,
                          onClick: onClickZoomIn,
                        },
                        {
                          key: 3,
                          content: <ZoomOutIcon />,
                          onClick: onClickZoomOut,
                        },
                        {
                          key: 4,
                          content: `${roundToPrecision((scale / 1.5) * 100, 0)}%`,
                        },
                      ]}
                    />
              </div>

              <div className="tool tool--voxel">
                <div id="upperLeftInfoBar" className="tools-list-row segmentation-tools-rows d-flex align-items-center justify-content-between"> Voxel: 0 HU </div>
              </div>

              <div className="tool tool--actions">
                  <ActionButton
                  onClick={uploadHandler} 
                  disabled={isSavingFile}
                  text={translate('submit', language)}
                  icon={<SendIcon />}>
                  </ActionButton>

                  <ActionButton 
                    theme={"light"}
                    onClick={saveHandler} 
                    disabled={isSavingFile} 
                    text={translate('donwload', language)}
                    icon={<DownloadIcon />}>
                  </ActionButton>
              </div>
            </div>

            <div className="inputs-row inputs-row--bottom">
              <div className="tool tool--dimension">
                <OptionList
                  theme="dark"
                  rounded
                  options={[
                    {
                      key: 1,
                      content: '2D',
                      active: is3D == false,
                      onClick: onClick3D,
                    },
                    {
                      key: 2,
                      content: '3D',
                      active: is3D == true,
                      onClick: onClick3D,
                    },
                  ]}
                /> 
              </div>

              <div className="tool tool--diameter">
                <InputNumberField
                  theme="dark"
                  type="number"
                  inputName="segmentation-diameter"
                  value={segmentationDiameter}
                  handleChange={setSegmentationDiameter}
                  suffix=" mm"
                  max={MAX_SPHERE_DIAMETER}
                  min={MIN_SPHERE_DIAMETER}
                />
              </div>

              <div className="tool tool--undo">
                <OptionList 
                  theme="dark"
                  rounded
                  options={[
                    {
                      key: 1,
                      content: <UndoIcon />,
                      onClick: window.desfazerAlteracao,
                    },
                    {
                      key: 2,
                      content: <RefreshIcon />,
                      onClick: onClickRefresh,
                    }
                  ]}
                />
              </div>

              <div className="tool tool--density">
                <DensityTypesSelect
                      types={densityOptions}
                      selected={selectedType}
                      placeholder={translate('selectAnOption', language)}
                      handleSelect={handleSelectType}
                />
              </div>

              <div className="tool tool--sliders-group">
                <Label htmlFor="sliderDensityMin" className="pb-0" style={{ width: '100%' }}>
                  <Span style={{ fontSize: '12px', display: 'flex', justifyContent: 'center' }}>{translate('densityControl', language)}</Span>
                  <div className="density-limits-wrapper">
                    <p className="density-limits-value me-2">{minDensityLimits}</p>

                    <div className="density-limits-slider">
                      <div className="slider-track"></div>

                      {setMinDensityLimits && (
                        <DensityRange
                          type="range"
                          className="sliderDensityMin"
                          id="sliderDensityMin"
                          value={minDensityLimits}
                          max={2000}
                          min={-2000}
                          onChange={(e) => onChangeInputRangeDensityMin(e)}
                        />
                      )}

                      {setMaxDensityLimits && (
                        <DensityRange
                          type="range"
                          className="sliderDensityMax"
                          id="sliderDensityMax"
                          value={maxDensityLimits}
                          max={2000}
                          min={-2000}
                          onChange={(e) => onChangeInputRangeDensityMax(e)}
                        />
                      )}
                    </div>

                    <p className="density-limits-value ms-2">{maxDensityLimits}</p>
                  </div>
                </Label>
              </div>

              <div className="tool tool--sliders-group">
                <Label htmlFor="myRange" className="pb-0" style={{ width: '100%' }}>
                  <Span style={{ fontSize: '12px', display: 'flex', justifyContent: 'center' }}>Slice</Span>
                  <div className="tools-list-row segmentation-tools-rows d-flex align-items-center justify-content-between">
                    <button
                      className="btn btn-outline-light btn-sm me-2"
                      onClick={() => handleSliceChange(actualSlice - 1)}
                    >
                      –
                    </button>
                    <Span className="me-2">1</Span>
                    <div className="slider-wrapper position-relative w-100">
                      <Range
                        ref={ref}
                        type="range"
                        className="slider"
                        id="myRange"
                        label="Slice"
                        value={actualSlice || 1}
                        max={maxSlice}
                        onChange={(e) => handleSliceChange(Number(e.target.value))}
                      />
                      <div
                        className="slider-tooltip"
                        style={{
                          left: `calc(${(actualSlice / maxSlice) * 100}%)`,
                        }}
                      >
                        {actualSlice}
                      </div>
                    </div>

                    <Span className="ms-2">{maxSlice}</Span>

                    <button
                      className="btn btn-outline-light btn-sm ms-2"
                      onClick={() => handleSliceChange(actualSlice + 1)}
                    >
                      +
                    </button>
                  </div>
                </Label>
              </div>

            </div>

              {/* <InputFile
                fileIndex={null}
                id="datafile"
                name="datafile"
                spanText="Selecione o arquivo"
                inputRef={dataFileRef}
              />
              <InputFile
                fileIndex={1}
                id="maskfile"
                name="maskfile"
                spanText="Selecione a segmentação"
                inputRef={maskFileRef}
              />
              <InputFile
                fileIndex={2}
                id="segfile"
                name="segfile"
                spanText="Selecione a classificação"
                inputRef={segFileRef}
              />*/}
                        
              {/* <div className="save-button-wrapper">
                {handleSave ? (
                  <ActionButton
                    theme="default"
                    text="Save"
                    icon={<CheckIcon />}
                    iconPosition="left"
                    onClick={handleSave}
                  />
                ) : null}
              </div> */}
      
          </InputsWrapper>

            <OriginalPreview id="originalPreview"
              width="512"
              height="512"
              onClick={mouseOnClickFunction}
              onWheel={mouseWheel}
              style={{
                transform: `scale(-${scale},${scale}) rotate(180deg)` 
              }}
            />

            <EditorPreviewWrapper>
              <EditorPreview
                id="myCanvas"
                width="512"
                height="512"
                style={{
                  transform: `scale(-${scale},${scale}) rotate(180deg)` 
                }}
              />

              <EditorPreview
                id="maskCanvas"
                width="512"
                height="512"
                onMouseDown={mouseDownFunction}
                onMouseMove={window.mouseMoves}
                onMouseUp={window.mouseUpFunction}
                onContextMenu={(e) => e.preventDefault()}
                onClick={mouseOnClickFunction}
                onWheel={mouseWheel}
                style={{
                  transform: `scale(-${scale},${scale}) rotate(180deg)` 
                }}
              />
            </EditorPreviewWrapper>
        {/* <MaskiaCanvas id="maskiaCanvas"
            onClick={mouseOnClickFunction} 
            onWheel={mouseWheel}
            />
            <SegCanvas id="segCanvas"
            onClick={mouseOnClickFunction} 
            onWheel={mouseWheel}
            /> */}
        </CanvasWrapper>
      </div>
      {loading ? <Loading /> : null}
    </NiftiEditorToolStyle>
  );
};

NiftiEditorTool.propTypes = {
  modal: PropTypes.bool,
};

NiftiEditorTool.defaultProps = {
  modal: false,
};

export default NiftiEditorTool;